'use client';
import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { containerService, type ContainerModel } from '@/lib/services/containerService';

interface ContainerCardProps {
  model: ContainerModel;
}

const ContainerCard = ({ model }: ContainerCardProps) => (
  <div className="container-card p-6 hover:shadow-xl transition-shadow">
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="text-xl font-bold text-foreground mb-1">{model.name}</h3>
        <p className="text-sm text-muted-foreground">{model.dimensions}</p>
      </div>
      <div className="text-right">
        <div className="text-2xl font-bold text-primary">
          ${model.price.toLocaleString()}
        </div>
        <div className="text-xs text-muted-foreground line-through">
          ${model.originalPrice.toLocaleString()}
        </div>
      </div>
    </div>
    <div className="flex items-center gap-2 text-sm text-success">
      <Icon name="CheckCircleIcon" size={16} />
      <span>Save ${model.savings.toLocaleString()}</span>
    </div>
  </div>
);

const ModelsGridSection = () => {
  const [models, setModels] = useState<ContainerModel[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const data = await containerService.getAll();
        setModels(data.slice(0, 6)); // Show first 6 models
      } catch (error) {
        console.error('Failed to load container models:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, []);

  if (loading) {
    return (
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center">
            <Icon name="ArrowPathIcon" size={40} className="animate-spin text-primary mx-auto" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className="reveal text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold font-montserrat text-foreground mb-4">
            Our Container <span className="text-primary">Models</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            From compact 20ft units to spacious double 40ft containers. Find the perfect size for your business.
          </p>
        </div>

        {/* Models Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {models?.map((model, index) => (
            <div
              key={model.id}
              className="reveal"
              style={{ animationDelay: `${0.1 * (index + 1)}s` }}
            >
              <ContainerCard model={model} />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="reveal text-center">
          <a
            href="/models-pricing"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-8 py-4 hover:bg-primary/90 transition-all"
          >
            View All Models & Pricing
            <Icon name="ArrowRightIcon" size={20} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ModelsGridSection;